import useDispatchConfirmation from "./useDispatchConfirmation";
import React, { useRef, useState } from "react";
import { Button } from "primereact/button";
import { confirmedWrite } from "./api";
import { Field, Lookup, Select, Status, useData, TYPES, today } from "./common";
export default function MovementForm({
  mode = "new",
  source,
  onSaved,
  onClose,
}) {
  const { confirm, dialog: confirmationDialog } = useDispatchConfirmation();
  const request = mode === "request";
  const adjustment = mode === "adjust",
    linked = mode === "return" && source;
  const [form, setForm] = useState({
    type:
      mode === "return"
        ? "DEVOLUCION"
        : mode === "dispose"
        ? "DESINCORPORACION"
        : "ENTREGA_REGULAR",
    channel: mode === "dispose" ? "NINGUNO" : source?.channel || "RUTA",
    operationDate: today(),
    orderNumber: source?.orderNumber || "",
    exceptional: source?.exceptional || false,
    exceptionReason: source?.exceptionReason || "",
    exceptionDetail: source?.exceptionDetail || "",
    observations: "",
    reason: "",
    reasonDetail: "",
  });
  const [destination, setDestination] = useState(
    source?.destination
      ? { ...source.destination, _id: source.destination.destinationId }
      : null
  );
  const [customer, setCustomer] = useState(
    source?.customer
      ? { ...source.customer, displayName: source.customer.name }
      : null
  );
  const [operator, setOperator] = useState(
    source?.operator
      ? { ...source.operator, _id: source.operator.userId }
      : null
  );
  const [routeId, setRouteId] = useState("");
  const choices = useData(request ? "solicitudes/opciones" : "acceso", request ? { operatorId: operator?._id } : {});
  const routes = choices.data?.routes || [];
  const route = routes.find(row => row._id === routeId);
  const [recipient, setRecipient] = useState(
    source?.collectedBy?.userId
      ? { ...source.collectedBy, _id: source.collectedBy.userId }
      : source?.collectedBy?.name || ""
  );
  const [lines, setLines] = useState(
    (
      (adjustment ? source?.currentLines : source?.lines) ||
      source?.lines ||
      []
    ).map((l) => ({
      ...l,
      quantity: mode === "return" ? "" : l.quantity,
    }))
  );
  const [chosen, setChosen] = useState(null),
    [quantity, setQuantity] = useState("1");
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const searchRef = useRef(null),
    quantityRef = useRef(null);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const add = () => {
    if (!chosen?._id) {
      setError("Seleccione un insumo de la lista.");
      return;
    }
    if (lines.some((l) => l.itemId === chosen._id)) {
      setError("El insumo ya está agregado; edite su cantidad.");
      return;
    }
    if (
      !Number.isFinite(Number(quantity)) ||
      Number(quantity) === 0 ||
      Number(quantity) < 0 ||
      (!chosen.allowDecimals && !Number.isInteger(Number(quantity)))
    ) {
      setError("Cantidad inválida para la unidad seleccionada.");
      return;
    }
    setLines([
      ...lines,
      {
        itemId: chosen._id,
        code: chosen.code,
        name: chosen.name,
        presentation: chosen.presentation,
        unit: chosen.dispatchUnit,
        quantity: Number(quantity),
      },
    ]);
    setChosen(null);
    setQuantity("1");
    setError("");
    searchRef.current?.focus();
  };
  const save = async () => {
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      const enteredLines =
        mode === "return"
          ? lines.filter((l) => String(l.quantity).trim() !== "")
          : lines;
      if (!enteredLines.length)
        throw new Error(
          mode === "return"
            ? "Indique la cantidad de al menos un insumo devuelto."
            : adjustment
            ? "Indique la cantidad de al menos un insumo a ajustar."
            : "Agregue al menos un insumo."
        );
      for (const line of enteredLines) {
        const quantity = Number(line.quantity);
        if (
          String(line.quantity ?? "").trim() === "" ||
          !Number.isFinite(quantity) ||
          (!adjustment && quantity === 0) ||
          Math.abs(quantity) > 1e9 ||
          quantity < 0
        ) {
          throw new Error(
            `${line.code} · ${line.name}: indique una cantidad ${
              adjustment ? "mayor o igual a cero" : "mayor que cero"
            }.`
          );
        }
      }
      const body = {
        operationDate: form.operationDate,
        lines: enteredLines.map((l) => ({
          itemId: l.itemId,
          quantity: Number(l.quantity),
          observation: l.observation || "",
        })),
      };
      let path = "movimientos",
        method = "POST";
      if (adjustment) {
        body.reason = form.reason;
        body.quantityMode = "FINAL";
        body.version = source.version;
        path = `movimientos/${source._id}/ajuste`;
      } else {
        Object.assign(body, {
          type: form.type,
          channel: form.channel,
          state: request ? "SOLICITADO" : "ENTREGADO",
          exceptional: form.exceptional,
          observations: form.observations,
        });
        if (request) {
          if (!operator?._id || !route || !["RUTA", "CLIENTE"].includes(form.channel))
            throw new Error("Seleccione operador, canal y ruta asignada.");
          body.operatorRequest = true;
          body.requestRouteId = routeId;
          path = "solicitudes";
        }
        if (form.channel === "RUTA") {
          if (request ? !route?.destinationId : !destination?._id) throw new Error("Seleccione un destino.");
          body.destinationId = request ? route.destinationId : destination._id;
        }
        if (form.channel === "CLIENTE" && request) {
          if (!customer?._id) throw new Error("Seleccione un cliente de la ruta.");
          body.locationId = customer._id;
          if (form.orderNumber.trim()) body.orderNumber = form.orderNumber.trim();
        } else if (form.channel === "CLIENTE" && !source?.customer?.locationId) {
          if (!customer?.quickBooksId)
            throw new Error("Seleccione un cliente.");
          body.customer = {
            quickBooksId: customer.quickBooksId,
            realmId: customer.realmId,
            businessUnit: customer.businessUnit,
          };
          body.orderNumber = form.orderNumber;
        }
        if (form.channel !== "NINGUNO") {
          body.operatorId = operator?._id || null;
          if (typeof operator === "string" && operator.trim())
            throw new Error("Seleccione el titular de la lista o deje vacío.");
          if (recipient?._id) body.collectedBy = { userId: recipient._id };
          else if (typeof recipient === "string" && recipient.trim())
            body.collectedBy = { name: recipient.trim() };
        }
        if (form.exceptional) {
          body.exceptionReason = form.exceptionReason;
          body.exceptionDetail = form.exceptionDetail;
        }
        if (["DEVOLUCION", "DESINCORPORACION"].includes(form.type)) {
          body.reason = form.reason;
          if (form.reasonDetail) body.reasonDetail = form.reasonDetail;
        }
        if (linked || source?.originMovementId)
          body.originMovementId = linked ? source._id : source.originMovementId;
        if (form.type === "DEVOLUCION") path = "devoluciones";
        else if (form.type === "DESINCORPORACION") path = "desincorporaciones";
      }
      const saved = await confirmedWrite(path, method, body, confirm);
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      {confirmationDialog}
      <h2>
        {adjustment
          ? `Ajuste de #${source.sequence}`
          : mode === "return"
          ? "Registrar devolución"
          : mode === "dispose"
          ? "Desincorporación"
          : request ? "Solicitud de Pedido - Operador" : "Nuevo despacho"}
      </h2>
      {mode === "return" && (
        <p>
          Escriba solo lo devuelto. Las líneas que deje vacías no se
          registrarán.
        </p>
      )}
      {linked && (
        <p>
          Origen #{source.sequence}. Ajuste las cantidades que se devuelven.
        </p>
      )}
      <fieldset disabled={busy} className="sd-form">
        <div className="sd-grid">
          <Field title="Fecha de operación">
            <input
              aria-label="Fecha de operación"
              type="date"
              value={form.operationDate}
              onChange={(e) => set("operationDate", e.target.value)}
            />
          </Field>
          {!adjustment && form.channel !== "NINGUNO" && (
            <>
              {request && (
                <>
                  <Lookup title="Operador titular" path="operadores" value={operator}
                    onChange={(v) => { setOperator(v); setRouteId(""); setCustomer(null); set("channel", ""); setRecipient(v && typeof v === "object" ? v : ""); }} />
                  <Status {...choices} />
                </>
              )}
              <Select
                title="Canal"
                value={form.channel}
                disabled={!!linked}
                options={request ? [{ value: "RUTA", label: "Ruta" }, { value: "CLIENTE", label: "Cliente" }] : ["RUTA", "CLIENTE"]}
                onChange={(v) => {
                  set("channel", v);
                  if (request) { setRouteId(""); setCustomer(null); }
                  else { setOperator(null); setRecipient(""); }
                }}
              />
              {form.type !== "DEVOLUCION" && (
                <Select
                  title="Tipo"
                  value={form.type}
                  options={TYPES.slice(0, 4)}
                  onChange={(v) => set("type", v)}
                />
              )}
              {request ? (
                <>
                  <Select title="Destino" value={routeId} disabled={!operator?._id || !form.channel}
                    options={routes.map(row => ({ value: row._id, label: row.name }))}
                    onChange={(v) => { setRouteId(v); setCustomer(null); }} />
                  {form.channel === "CLIENTE" && <Lookup key={String(operator?._id) + routeId}
                    title="Cliente" path="solicitudes/clientes" value={customer} onChange={setCustomer}
                    disabled={!routeId} query={{ operatorId: operator?._id, routeId }} />}
                </>
              ) : linked ? (
                <p>
                  {source.destination?.name || source.customer?.name} · Orden{" "}
                  {source.orderNumber || "—"}
                </p>
              ) : form.channel === "RUTA" ? (
                <Lookup
                  title="Destino"
                  path="destinos"
                  value={destination}
                  onChange={(v) => {
                    setDestination(v);
                    if (v && typeof v === "object") {
                      const person = v.operatorId
                        ? {
                            _id: v.operatorId,
                            name: v.operatorName || "Titular asignado",
                          }
                        : null;
                      setOperator(person);
                      setRecipient(person || "");
                    }
                  }}
                />
              ) : (
                <Lookup
                  title="Cliente"
                  path="clientes"
                  value={customer}
                  onChange={setCustomer}
                />
              )}
              {form.channel === "CLIENTE" && (
                <Field title="Número de orden">
                  <input
                    value={form.orderNumber}
                    disabled={!!linked}
                    onChange={(e) => set("orderNumber", e.target.value)}
                  />
                </Field>
              )}
              {!request && <Lookup
                title="Operador titular (opcional)"
                path="operadores"
                value={operator}
                onChange={(v) => {
                  setOperator(v);
                  if (v && typeof v === "object") setRecipient(v);
                }}
              />
              }
              <Lookup
                title="Quién retira (usuario o nombre libre)"
                path="operadores"
                value={recipient}
                onChange={setRecipient}
              />
            </>
          )}
        </div>
        {!adjustment && (
          <>
            <Field title="Excepcional">
              <input
                type="checkbox"
                checked={form.exceptional}
                onChange={(e) => set("exceptional", e.target.checked)}
              />
            </Field>
            {form.exceptional && (
              <div className="sd-grid">
                <Select
                  title="Motivo de excepción"
                  value={form.exceptionReason}
                  options={["EVENTO", "PRUEBA", "INSTALACION_NUEVA", "OTRO"]}
                  onChange={(v) => set("exceptionReason", v)}
                />
                <Field title="Detalle de excepción">
                  <input
                    value={form.exceptionDetail}
                    onChange={(e) => set("exceptionDetail", e.target.value)}
                  />
                </Field>
              </div>
            )}
          </>
        )}
        {(adjustment ||
          ["DEVOLUCION", "DESINCORPORACION"].includes(form.type)) && (
          <div className="sd-grid">
            {adjustment ? (
              <Field title="Motivo del ajuste (mínimo 10 caracteres)">
                <input
                  value={form.reason}
                  onChange={(e) => set("reason", e.target.value)}
                />
              </Field>
            ) : (
              <Select
                title="Motivo"
                value={form.reason}
                options={
                  form.type === "DEVOLUCION"
                    ? ["SOBRANTE", "CALIDAD", "ERROR_DESPACHO", "OTRO"]
                    : ["VENCIMIENTO", "DANO", "MERMA", "OTRO"]
                }
                onChange={(v) => set("reason", v)}
              />
            )}
            {form.reason === "OTRO" && (
              <Field title="Detalle del motivo">
                <input
                  value={form.reasonDetail}
                  onChange={(e) => set("reasonDetail", e.target.value)}
                />
              </Field>
            )}
          </div>
        )}
        {adjustment && (
          <p>
            Se muestran las cantidades vigentes, incluidos los ajustes
            anteriores. Escriba cómo debe quedar cada cantidad; el sistema
            registrará solo la diferencia. Para quitar un insumo, indique 0.
          </p>
        )}
        <div
          className="sd-lines sd-table-scroll"
          role="region"
          aria-label="Insumos del movimiento"
          tabIndex={0}
        >
          <table>
            <thead>
              <tr>
                <th>Insumo / presentación</th>
                <th>Unidad</th>
                <th>Cantidad</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.itemId}>
                  <td>
                    {l.code} · {l.name}
                    <small>{l.presentation}</small>
                  </td>
                  <td>{l.unit}</td>
                  <td>
                    <input
                      aria-label={`Cantidad ${l.code}`}
                      type="number"
                      step="any"
                      value={l.quantity}
                      onChange={(e) =>
                        setLines(
                          lines.map((x, j) =>
                            j === i ? { ...x, quantity: e.target.value } : x
                          )
                        )
                      }
                    />
                  </td>
                  <td>
                    <Button
                      aria-label={`Quitar ${l.code}`}
                      icon="pi pi-times"
                      className="p-button-text"
                      onClick={() =>
                        setLines(
                          adjustment
                            ? lines.map((line, j) =>
                                i === j ? { ...line, quantity: 0 } : line
                              )
                            : lines.filter((_, j) => i !== j)
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="sd-grid sd-add">
          <Lookup
            title="Buscar insumo por código o nombre"
            path="insumos"
            inputRef={searchRef}
            value={chosen}
            onChange={(v) => {
              setChosen(v);
              if (v && typeof v === "object")
                setTimeout(() => {
                  quantityRef.current?.focus();
                  quantityRef.current?.select();
                }, 0);
            }}
          />
          <Field title={`Cantidad · ${chosen?.dispatchUnit || "unidad"}`}>
            <input
              ref={quantityRef}
              aria-label="Cantidad nueva"
              type="number"
              step={chosen?.allowDecimals ? "any" : "1"}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
            />
          </Field>
          <Button label="Agregar línea" icon="pi pi-plus" onClick={add} />
        </div>
        {!adjustment && (
          <Field title="Observaciones">
            <textarea
              value={form.observations}
              onChange={(e) => set("observations", e.target.value)}
            />
          </Field>
        )}
      </fieldset>
      {error && (
        <p className="sd-error" role="alert">
          {error}
        </p>
      )}
      <div className="sd-toolbar">
        <Button
          label="Volver"
          className="p-button-secondary"
          disabled={busy}
          onClick={onClose}
        />
        <Button
          label={
            adjustment
              ? "Registrar ajuste"
              : form.type === "DESINCORPORACION"
              ? "Registrar desincorporación"
              : form.type === "DEVOLUCION"
              ? "Registrar devolución"
              : request ? "Guardar como solicitado" : "Guardar como entregado"
          }
          className="sd-primary-save"
          loading={busy}
          onClick={save}
        />
      </div>
    </section>
  );
}

